import React, { useState } from "react";

const PotionTrainerModal = ({
  isOpen,
  toggle,
  trainerItems,
  trainerInfo,
  loadingTrainerInfo,
  currency,
  buyAmounts,
  handleTrainerQuantityChange,
  handleTrainerPurchase,
  trainerType = "trainer-potion"
}) => {
  const [activeCategory, setActiveCategory] = useState('all');

  // Helper to build categories based on trainer type
  const categories = ['resources', 'all', 'potions'];

  // Helper to build price display arrays - include battle coins
  const currencyKeys = ['money', 'coins', 'ruby', 'sapphire', 'emerald', 'gold', 'diamond'];

  const getImgSrc = (item) => {
    if (item.image) return `/${item.image}`;
    return `/items/${item.id}.png`;
  };

  const hasCost = (item) => {
    // For potions and revives, we don't require a cost since they might be free
    // The potion field represents healing value, not price
    return currencyKeys.some(k => (item[k] || 0) > 0) || (item.other || 0) > 0 || 
           [29, 30, 31, 32, 33, 84, 85,193].includes(item.id); // Always show potions and revives regardless of cost
  };

  // Calculate total price of an item for sorting
  const calculateTotalPrice = (item) => {
    let totalPrice = 0;
    // Add money value
    totalPrice += item.money || 0;
    // Add coins value (convert to money equivalent)
    totalPrice += (item.coins || 0) * 100;
    // Add battle coins value (convert to money equivalent)
    totalPrice += (item.other || 0) * 200;
    // Add other currencies (rough conversion)
    if (item.ruby) totalPrice += item.ruby * 1000;
    if (item.sapphire) totalPrice += item.sapphire * 1000;
    if (item.emerald) totalPrice += item.emerald * 1000;
    if (item.gold) totalPrice += item.gold * 1000;
    if (item.diamond) totalPrice += item.diamond * 1000;
    
    // If price is 0, give it a default value based on ID to maintain consistent order
    return totalPrice === 0 ? item.id : totalPrice;
  };

  const getCurrencyIcon = (key) => {
    const icons = {
      money: "/items/money.png",
      coins: "/items/coin.png", 
      battle_coins: "/items/battle_coin.png",
      ruby: "/items/ruby.png",
      sapphire: "/items/sapphire.png",
      emerald: "/items/emerald.png",
      gold: "/items/gold.png",
      diamond: "/items/diamond.png"
    };
    return icons[key] || "/items/coin.png";
  };

  const getCurrencyName = (key) => {
    const names = {
      money: "Money",
      coins: "Coins",
      battle_coins: "Battle Coins",
      ruby: "Ruby",
      sapphire: "Sapphire", 
      emerald: "Emerald",
      gold: "Gold",
      diamond: "Diamond"
    };
    return names[key] || key;
  };

  const getCurrencyColor = (key) => {
    const colors = {
      money: 'rgba(40, 167, 69, 0.2)',
      coins: 'rgba(255, 193, 7, 0.2)',
      battle_coins: 'rgba(74, 144, 226, 0.2)',
      ruby: 'rgba(220, 53, 69, 0.2)',
      sapphire: 'rgba(13, 110, 253, 0.2)',
      emerald: 'rgba(25, 135, 84, 0.2)',
      gold: 'rgba(255, 193, 7, 0.2)',
      diamond: 'rgba(156, 39, 176, 0.2)'
    };
    return colors[key] || 'rgba(108, 117, 125, 0.2)';
  };

  const getCurrencyBorderColor = (key) => {
    const colors = {
      money: 'rgba(40, 167, 69, 0.3)',
      coins: 'rgba(255, 193, 7, 0.3)',
      battle_coins: 'rgba(74, 144, 226, 0.3)',
      ruby: 'rgba(220, 53, 69, 0.3)',
      sapphire: 'rgba(13, 110, 253, 0.3)',
      emerald: 'rgba(25, 135, 84, 0.3)',
      gold: 'rgba(255, 193, 7, 0.3)',
      diamond: 'rgba(156, 39, 176, 0.3)'
    };
    return colors[key] || 'rgba(108, 117, 125, 0.3)';
  };

  if (!isOpen) return null;

  return (
    <div 
      className="game-popup-overlay"
      onClick={toggle}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px 10px',
        boxSizing: 'border-box',
        zIndex: 1050
      }}
    >
      <div 
        className="potion-trainer-popup"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '95%',
          maxWidth: 900,
          maxHeight: '95vh',
          background: 'rgba(29, 34, 46, 0.95)',
          color: '#fff',
          borderRadius: 12,
          backdropFilter: 'blur(10px)',
          border: '2px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          margin: '10px'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '12px 16px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'rgba(0, 0, 0, 0.2)',
          flexWrap: 'wrap',
          gap: '8px',
          minHeight: '50px'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            flex: '1 1 auto',
            minWidth: '200px'
          }}>
        
            <h3 style={{ 
              margin: 0, 
              fontSize: '18px', 
              fontWeight: 'bold',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>Training Store</h3>
          </div>
          <button
            onClick={toggle}
            style={{
              background: 'none',
              border: 'none',
              color: '#fff',
              fontSize: '20px',
              cursor: 'pointer',
              padding: '8px 12px',
              borderRadius: '4px',
              opacity: 0.7,
              flexShrink: 0,
              minWidth: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => e.target.style.opacity = '1'}
            onMouseLeave={(e) => e.target.style.opacity = '0.7'}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{
          padding: '20px',
          overflowY: 'auto',
          flex: 1
        }}>
          {loadingTrainerInfo ? (
            <div style={{ textAlign: 'center', padding: '40px', fontSize: '16px', opacity: 0.8 }}>
              Loading  store...
            </div>
          ) : (
            <div>
              {/* Category tabs */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
                {categories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    style={{
                      padding: '8px 16px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      background: activeCategory === cat 
                        ? 'linear-gradient(135deg, #007bff, #0056b3)' 
                        : 'rgba(255, 255, 255, 0.1)',
                      color: activeCategory === cat ? '#fff' : 'rgba(255, 255, 255, 0.8)',
                      boxShadow: activeCategory === cat 
                        ? '0 2px 8px rgba(0, 123, 255, 0.3)' 
                        : 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (activeCategory !== cat) {
                        e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (activeCategory !== cat) {
                        e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                      }
                    }}
                  >
                    {cat === 'resources' ? 'Resources' : 
                     cat === 'all' ? 'All Items' :
                     cat === 'potions' ? 'Potions' : 
                
                     cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </button>
                ))}
              </div>
              {/* Resources view */}
              {activeCategory === 'resources' && (
                <div>
                  <h5 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 'bold', color: '#fff' }}>Your Resources</h5>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', 
                    gap: '12px',
                    '@media (max-width: 768px)': {
                      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                      gap: '8px'
                    }
                  }}>
                    {/* Battle Coins */}
                    <div style={{ 
                      background: getCurrencyColor('battle_coins'), 
                      border: `1px solid ${getCurrencyBorderColor('battle_coins')}`, 
                      borderRadius: '8px', 
                      padding: '12px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      minWidth: 0,
                      overflow: 'hidden'
                    }}>
                      <img src={getCurrencyIcon('battle_coins')} alt="Battle Coins" style={{ width: 24, height: 24, flexShrink: 0 }} />
                      <span style={{ 
                        fontSize: '14px', 
                        fontWeight: 'bold',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0
                      }}>
                        {(currency.battle_coins || 0).toLocaleString()} Battle Coins
                      </span>
                    </div>
                    {/* Other currencies */}
                    {currencyKeys.map(key => (
                      <div key={key} style={{ 
                        background: getCurrencyColor(key), 
                        border: `1px solid ${getCurrencyBorderColor(key)}`, 
                        borderRadius: '8px', 
                        padding: '12px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px',
                        minWidth: 0,
                        overflow: 'hidden'
                      }}>
                        <img src={getCurrencyIcon(key)} alt={getCurrencyName(key)} style={{ width: 24, height: 24, flexShrink: 0 }} />
                        <span style={{ 
                          fontSize: '14px', 
                          fontWeight: 'bold',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          minWidth: 0
                        }}>
                          {key === 'money' ? `$${(currency[key] || 0).toLocaleString()}` : `${(currency[key] || 0).toLocaleString()} ${getCurrencyName(key)}`}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* All Items view */}
              {activeCategory === 'all' && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                  {(trainerItems || [])
                    .filter(item => {
                      // Filter for all healing items (potions and revives)
                      return [29, 30, 31, 32, 33, 84, 85,153,193].includes(item.id) || 
                             item.name?.toLowerCase().includes('potion') || 
                             item.name?.toLowerCase().includes('restore') ||
                             item.name?.toLowerCase().includes('revive');
                    })
                    .filter(item => hasCost(item))
                    // Sort by price (cheapest to most expensive)
                    .sort((a, b) => calculateTotalPrice(a) - calculateTotalPrice(b))
                    .map(item => {
                      const quantity = buyAmounts[item.id] || 1;
                      const costs = {};
                      
                      // Handle 'other' field (battle coins)
                      const otherCost = (item.other || 0) * quantity;
                      if (otherCost > 0) {
                        costs.other = otherCost;
                      }
                      
                      // Handle other currencies
                      currencyKeys.forEach(k => {
                        costs[k] = (item[k] || 0) * quantity;
                      });
                      
                      // Check affordability including battle coins from 'other' field
                      const canAffordOther = otherCost === 0 || (currency.battle_coins || 0) >= otherCost;
                      const canAffordRegular = currencyKeys.every(k => (currency[k] || 0) >= costs[k]);
                      const canAfford = canAffordOther && canAffordRegular;
                      
                      // Check if this is Battle Charm
                      const isBattleCharm = item.id === 193;
                      
                      return (
                        <div key={item.id} style={{
                          background: 'rgba(0, 0, 0, 0.3)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: '8px',
                          padding: '12px',
                          display: 'flex',
                          gap: '12px',
                          alignItems: 'flex-start'
                        }}>
                          <div style={{ flexShrink: 0 }}>
                            <img src={getImgSrc(item)} alt={item.name} style={{width:40,height:40,imageRendering:'pixelated'}} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <h6 style={{ margin: '0 0 4px', fontSize: '14px', fontWeight: 'bold', color: '#fff' }}>{item.name}</h6>
                            <p style={{ margin: '0 0 8px', fontSize: '11px', opacity: 0.8, lineHeight: '1.3' }}>{item.desc || item.description || 'No description.'}</p>
                            
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                              {/* Show battle coins cost from 'other' field */}
                              {costs.other > 0 && (
                                <span style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: '4px', 
                                  fontSize: '11px', 
                                  background: getCurrencyColor('battle_coins'), 
                                  padding: '2px 6px', 
                                  borderRadius: '4px' 
                                }}>
                                  <img src={getCurrencyIcon('battle_coins')} alt="Battle Coins" style={{ width: 12, height: 12 }} />
                                  {costs.other.toLocaleString()}
                                  {isBattleCharm && <span style={{ marginLeft: '4px', fontSize: '10px', opacity: 0.9 }}>(stack up to 10)</span>}
                                </span>
                              )}
                              {/* Show other currencies */}
                              {currencyKeys.map(k => costs[k] > 0 && (
                                <span key={k} style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: '4px', 
                                  fontSize: '11px', 
                                  background: getCurrencyColor(k), 
                                  padding: '2px 6px', 
                                  borderRadius: '4px' 
                                }}>
                                  <img src={getCurrencyIcon(k)} alt={getCurrencyName(k)} style={{ width: 12, height: 12 }} />
                                  {k === 'money' ? `$${costs[k].toLocaleString()}` : costs[k].toLocaleString()}
                                  {isBattleCharm && k === 'money' && <span style={{ marginLeft: '4px', fontSize: '10px', opacity: 0.9 }}>(stack up to 10)</span>}
                                </span>
                              ))}
                            </div>
                            
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <label style={{ fontSize: '11px', opacity: 0.8 }}>Qty:</label>
                                <input 
                                  type="number" 
                                  min="1" 
                                  max="99" 
                                  value={quantity}
                                  onChange={(e) => handleTrainerQuantityChange(item.id, e.target.value)}
                                  style={{
                                    width: '50px',
                                    padding: '2px 4px',
                                    fontSize: '11px',
                                    background: 'rgba(0, 0, 0, 0.5)',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    borderRadius: '4px',
                                    color: '#fff',
                                    textAlign: 'center'
                                  }}
                                />
                              </div>
                              <button
                                onClick={() => handleTrainerPurchase(item)}
                                disabled={!canAfford}
                                style={{
                                  padding: '4px 12px',
                                  fontSize: '11px',
                                  fontWeight: 'bold',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: canAfford ? 'pointer' : 'not-allowed',
                                  background: canAfford ? 'linear-gradient(135deg, #28a745, #20c997)' : 'rgba(108, 117, 125, 0.5)',
                                  color: canAfford ? '#fff' : 'rgba(255, 255, 255, 0.5)',
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                Buy
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}

              {/* Potions view */}
              {activeCategory === 'potions' && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                  {(trainerItems || [])
                    .filter(item => {
                      // Filter for potions only (IDs 29-32: Potion, Super Potion, Hyper Potion, Max Potion)
                      return [29, 30, 31, 32].includes(item.id) || 
                             (item.name?.toLowerCase().includes('potion') && !item.name?.toLowerCase().includes('revive'));
                    })
                    .filter(item => hasCost(item))
                    // Sort by price (cheapest to most expensive)
                    .sort((a, b) => calculateTotalPrice(a) - calculateTotalPrice(b))
                    .map(item => {
                      const quantity = buyAmounts[item.id] || 1;
                      const costs = {};
                      
                      // Handle 'other' field (battle coins)
                      const otherCost = (item.other || 0) * quantity;
                      if (otherCost > 0) {
                        costs.other = otherCost;
                      }
                      
                      // Handle other currencies
                      currencyKeys.forEach(k => {
                        costs[k] = (item[k] || 0) * quantity;
                      });
                      
                      // Check affordability including battle coins from 'other' field
                      const canAffordOther = otherCost === 0 || (currency.battle_coins || 0) >= otherCost;
                      const canAffordRegular = currencyKeys.every(k => (currency[k] || 0) >= costs[k]);
                      const canAfford = canAffordOther && canAffordRegular;
                      
                      // Check if this is Battle Charm
                      const isBattleCharm = item.id === 193;
                      
                      return (
                        <div key={item.id} style={{
                          background: 'rgba(0, 0, 0, 0.3)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: '8px',
                          padding: '12px',
                          display: 'flex',
                          gap: '12px',
                          alignItems: 'flex-start'
                        }}>
                          <div style={{ flexShrink: 0 }}>
                            <img src={getImgSrc(item)} alt={item.name} style={{width:40,height:40,imageRendering:'pixelated'}} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <h6 style={{ margin: '0 0 4px', fontSize: '14px', fontWeight: 'bold', color: '#fff' }}>{item.name}</h6>
                            <p style={{ margin: '0 0 8px', fontSize: '11px', opacity: 0.8, lineHeight: '1.3' }}>{item.desc || item.description || 'No description.'}</p>
                            
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                              {/* Show battle coins cost from 'other' field */}
                              {costs.other > 0 && (
                                <span style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: '4px', 
                                  fontSize: '11px', 
                                  background: getCurrencyColor('battle_coins'), 
                                  padding: '2px 6px', 
                                  borderRadius: '4px' 
                                }}>
                                  <img src={getCurrencyIcon('battle_coins')} alt="Battle Coins" style={{ width: 12, height: 12 }} />
                                  {costs.other.toLocaleString()}
                                  {isBattleCharm && <span style={{ marginLeft: '4px', fontSize: '10px', opacity: 0.9 }}>(stack up to 10)</span>}
                                </span>
                              )}
                              {/* Show other currencies */}
                              {currencyKeys.map(k => costs[k] > 0 && (
                                <span key={k} style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: '4px', 
                                  fontSize: '11px', 
                                  background: getCurrencyColor(k), 
                                  padding: '2px 6px', 
                                  borderRadius: '4px' 
                                }}>
                                  <img src={getCurrencyIcon(k)} alt={getCurrencyName(k)} style={{ width: 12, height: 12 }} />
                                  {k === 'money' ? `$${costs[k].toLocaleString()}` : costs[k].toLocaleString()}
                                  {isBattleCharm && k === 'money' && <span style={{ marginLeft: '4px', fontSize: '10px', opacity: 0.9 }}>(stack up to 10)</span>}
                                </span>
                              ))}
                            </div>
                            
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <label style={{ fontSize: '11px', opacity: 0.8 }}>Qty:</label>
                                <input 
                                  type="number" 
                                  min="1" 
                                  max="99" 
                                  value={quantity}
                                  onChange={(e) => handleTrainerQuantityChange(item.id, e.target.value)}
                                  style={{
                                    width: '50px',
                                    padding: '2px 4px',
                                    fontSize: '11px',
                                    background: 'rgba(0, 0, 0, 0.5)',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    borderRadius: '4px',
                                    color: '#fff',
                                    textAlign: 'center'
                                  }}
                                />
                              </div>
                              <button
                                onClick={() => handleTrainerPurchase(item)}
                                disabled={!canAfford}
                                style={{
                                  padding: '4px 12px',
                                  fontSize: '11px',
                                  fontWeight: 'bold',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: canAfford ? 'pointer' : 'not-allowed',
                                  background: canAfford ? 'linear-gradient(135deg, #28a745, #20c997)' : 'rgba(108, 117, 125, 0.5)',
                                  color: canAfford ? '#fff' : 'rgba(255, 255, 255, 0.5)',
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                Buy
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PotionTrainerModal;
